'use client';

import * as z from 'zod';
import { toast } from 'sonner';
import { useForm } from 'react-hook-form';
import { useRef, useState } from 'react';
import Link from 'next/link';
import { Mail } from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';

import { track } from '@/lib/analytics';
import { siteConfig } from '@/config/site';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Eyebrow } from '@/components/custom/section-shell';

const formSchema = z.object({
  name: z.string().min(3, 'Escribí al menos 3 letras'),
  email: z.string().email('Email inválido'),
  subject: z.string().min(5, 'Escribí al menos 5 letras'),
  message: z.string().min(10, 'Escribí al menos 10 letras'),
  consent: z.boolean().refine((val) => val === true, {
    message: 'Necesitamos tu consentimiento para contactarte.',
  }),
});

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Honeypot: bots fill hidden fields; humans never see it. We bail silently.
  const honeypotRef = useRef<HTMLInputElement>(null);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      email: '',
      subject: 'Contacto desde la web',
      message: '',
      consent: false,
    },
  });

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    if (honeypotRef.current?.value) {
      // Pretend it worked so bots don't retry.
      form.reset();

      return;
    }

    setIsSubmitting(true);

    try {
      const res = await fetch(`${siteConfig.apiUrl}/emails/contact`, {
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (res.ok) {
        track('contact_submitted');
        toast('¡Mensaje enviado! Gracias por tu mensaje, te contactaremos pronto.');
        form.reset();

        return;
      }

      toast.error('Algo salió mal al enviar el mensaje.');
    } catch {
      toast.error('No pudimos enviar tu mensaje, revisá tu conexión a internet.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contacto"
      className="bg-secondary py-[var(--section-y-sm)] md:py-[var(--section-y)]"
    >
      <div className="mx-auto grid w-full max-w-[var(--container)] grid-cols-1 gap-12 px-6 lg:grid-cols-2 lg:items-center lg:gap-16">
        <div className="flex flex-col items-start gap-6">
          <Eyebrow>Contacto</Eyebrow>
          <h2 className="font-display text-h2 font-bold tracking-[-0.02em] text-ink">
            Escribinos y te respondemos.
          </h2>
          <p className="max-w-md text-lead text-ink-soft">
            Contanos qué necesitás para tu taller. Te contestamos a la brevedad, sin vueltas.
          </p>
          <div className="flex items-center gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-brand text-brand-ink">
              <Mail className="size-4" />
            </span>
            <div>
              <p className="font-medium text-ink">Email</p>
              <Link
                href="mailto:contacto@mendly.com.ar"
                className="text-ink-soft transition-colors hover:text-brand"
              >
                contacto@mendly.com.ar
              </Link>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-line bg-surface p-8 shadow-[var(--shadow-md)]">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* Honeypot — hidden from humans, catches bots. */}
              <div aria-hidden="true" className="hidden">
                <label htmlFor="website">No completar</label>
                <input
                  ref={honeypotRef}
                  id="website"
                  name="website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nombre</FormLabel>
                      <FormControl>
                        <Input placeholder="Nombre" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input placeholder="tu@email.com" type="email" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="subject"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Asunto</FormLabel>
                    <FormControl>
                      <Input placeholder="¿Sobre qué se trata?" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="message"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Mensaje</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Contanos tu consulta acá..."
                        className="min-h-[120px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="consent"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-start gap-3">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          className="mt-0.5"
                        />
                      </FormControl>
                      <FormLabel className="text-sm font-normal leading-snug text-ink-soft">
                        Acepto que Mendly use mis datos para responder mi consulta, según la{' '}
                        <Link
                          href="https://docs.mendly.com.ar/privacy-policy"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-brand underline underline-offset-2 hover:text-brand/80"
                        >
                          Política de privacidad
                        </Link>
                        .
                      </FormLabel>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                size="lg"
                variant="brand"
                type="submit"
                className="w-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Cargando...' : 'Enviar mensaje'}
              </Button>
            </form>
          </Form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
